/**
 * The window the figures are asked for: two inclusive calendar days in ISO
 * form, exactly what the backend parses. Every window ends no later than the
 * last day published to the viewer — the backend pulls a later end back, and
 * the pickers here never offer one.
 */

export const MAX_WINDOW_DAYS = 366;
export const DEFAULT_WINDOW_DAYS = 30;
const DAY_MS = 86_400_000;

export interface DashboardWindow {
  from: string;
  to: string;
}

export interface RangePreset {
  key: string;
  label: string;
  days: number;
}

export const PRESETS: RangePreset[] = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: '1y', label: '1 year', days: 365 },
];

export function shiftDays(day: string, days: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** `days` long, both ends included, ending on the last published day. */
export function lastDays(days: number, publishedThrough: string): DashboardWindow {
  return { from: shiftDays(publishedThrough, -(days - 1)), to: publishedThrough };
}

export function windowDays({ from, to }: DashboardWindow): number {
  const start = new Date(`${from}T12:00:00Z`).getTime();
  const end = new Date(`${to}T12:00:00Z`).getTime();

  return Math.round((end - start) / DAY_MS) + 1;
}

/** Why the window cannot be asked for, in the viewer's words, or null. */
export function windowProblem(window: DashboardWindow): string | null {
  if (!window.from || !window.to) return 'Pick both a start and an end date.';
  if (window.from > window.to) return 'The start date is after the end date.';
  if (windowDays(window) > MAX_WINDOW_DAYS) return 'Pick a window of one year or less.';

  return null;
}
